import {EventEmitter, Injectable} from '@angular/core';
import {Socket} from 'ngx-socket-io';
import {environment} from '../../../environments/environment';
import {ApiService} from '../../app-common/services/server/api.service';
import {GenericHttpService} from '../../app-common/services/server/genericHttpService';
import {AiAssistantRegistrationService} from './ai-assistant-registration.service';
import {AuthService} from '../../auth/services/auth/auth.service';
import {ConfirmationService} from 'primeng/api';
import {IAiChatMessage} from '../components/ai-message/ai-message-view.model';

@Injectable({
  providedIn: 'root'
})
export class AiAssistantService {
  assistants?: AssistantDefinition[];
  chatUpdateEvent: EventEmitter<AIChatUpdateMessage> = new EventEmitter<AIChatUpdateMessage>();
  private apiClient: GenericHttpService;
  private socket: Socket;

  summarizationAiAssistant: AssistantDefinition = {
    name: 'Summer',
    description: 'Summer is an assistant that can summarize all kind of objects in the system.',
    image: 'https://avataaars.io/?avatarStyle=Transparent&topType=LongHairBob&accessoriesType=Prescription01&hairColor=BrownDark&facialHairType=Blank&clotheType=ShirtCrewNeck&clotheColor=PastelBlue&eyeType=Default&eyebrowType=Default&mouthType=Default&skinColor=Light',
    role: 'the Summarization Assistant',
    key: 'summer',
    availability: 'public'
  };

  generationAiAssistant: AssistantDefinition = {
    name: 'Genie',
    description: 'Genie is an assistant that can generate new content based on the current document.',
    image: 'https://avataaars.io/?avatarStyle=Transparent&topType=LongHairStraight&accessoriesType=Prescription01&hairColor=BlondeGolden&facialHairType=Blank&clotheType=ShirtCrewNeck&clotheColor=PastelBlue&eyeType=Default&eyebrowType=Default&mouthType=Default&skinColor=Light',
    role: 'the Generation Assistant',
    key: 'genie',
    availability: 'public'
  };

  disclaimerHtml: string = `
    By using this AI Solution provided by <span style="font-weight: bold;">GoDataHub.com</span>,
    you agree to take full responsibility for any actions you take based on the output received.
    <span style="font-weight: bold;">GoDataHub.com</span> is not liable for any damages, losses,
    or consequences that may result from your use of this AI Solution. This tool is provided "as is"
    without warranties of any kind, either express or implied.<br>
    For more details, please read our
    <a href="https://godatahub.com/legal/ai-disclaimer" target="_blank" style="color: #337ab7; text-decoration: underline;">
      full disclaimer
    </a>.
  `;

  llmRequests: Map<string, IAiChatMessage> = new Map<string, IAiChatMessage>();

  private readonly baseUrl = 'assistants/';

  constructor(private apiService: ApiService,
              private registrationTokenProvider: AiAssistantRegistrationService,
              private confirmationService: ConfirmationService,
              private authService: AuthService) {
    this.apiClient = apiService.aiAssistantApiClient;
    this.initialize();
  }

  async initialize() {
    this.subscribeToSocketIo();
    await this.getAssistants();
  }

  confirmLicenseAgreement() {
    return new Promise<void>(async (resolve, reject) => {
      const hasConfirmed = localStorage.getItem('ai-assistant-license-confirmed');
      if (hasConfirmed) {
        resolve();
        return;
      }
      this.showDisclaimer(true, () => {
        localStorage.setItem('ai-assistant-license-confirmed', 'true');
        resolve();
      }, () => {
        reject();
      });
    });
  }

  showDisclaimer(showButtons: boolean = true,
                 resolve: () => void = () => {},
                 reject: () => void = () => {}) {
    this.confirmationService.confirm({
      header: 'Disclaimer',
      icon: 'far fa-exclamation-triangle',
      message: this.disclaimerHtml,
      acceptLabel: 'I Agree',
      rejectLabel: 'I Disagree',
      acceptIcon: 'fas fa-check',
      rejectIcon: 'fas fa-times',
      acceptVisible: showButtons,
      rejectVisible: showButtons,
      accept: () => {
        localStorage.setItem('ai-assistant-license-confirmed', 'true');
        resolve();
      },
      reject: () => {
        reject();
      }
    });
  }

  async askQuestion(assistant: AssistantDefinition, question: string, modelToUpdate: IAiChatMessage) {
    const hasConfirmed = localStorage.getItem('ai-assistant-license-confirmed');
    if (!hasConfirmed) {
      try {
        await this.confirmLicenseAgreement();
        return this.askQuestion(assistant, question, modelToUpdate);
      } catch (e) {
        return {
          message: 'You must agree to the disclaimer to continue.',
          type: 'error'
        };
      }
    }
    let aiFocus = localStorage.getItem('ai-assistant-focus');
    if (!aiFocus) {
      aiFocus = 'speed';
    }
    const clientRequestId = Math.random().toString(36).substring(2, 15);
    // this.llmRequests.set(clientRequestId, modelToUpdate);
    const result = await this.apiClient.post<AIChatResult>(`${this.baseUrl}ask`, {
      aiFocus,
      question,
      assistant: assistant.key
    });
    modelToUpdate.finishStreaming();
    return result;
  }

  async summarize(entityType: string, entityId: number | string, focus: string, modelToUpdate: IAiChatMessage) {
    const hasConfirmed = localStorage.getItem('ai-assistant-license-confirmed');
    if (!hasConfirmed) {
      try {
        await this.confirmLicenseAgreement();
        return this.summarize(entityType, entityId, focus, modelToUpdate);
      } catch (e) {
        return {
          message: 'You must agree to the disclaimer to continue.',
          type: 'error'
        };
      }
    }
    const clientRequestId = Math.random().toString(36).substring(2, 15);
    this.llmRequests.set(clientRequestId, modelToUpdate);
    const result = await this.apiClient.post<AIChatResult>(`summarization/summarize`, {
      entityType,
      entityId,
      focus,
      clientRequestId
    });
    modelToUpdate.finishStreaming();
    return result;
  }

  async generate(entityType: string, entityId: number | string, focus: string, snippetId: number | undefined, modelToUpdate: IAiChatMessage) {
    const hasConfirmed = localStorage.getItem('ai-assistant-license-confirmed');
    if (!hasConfirmed) {
      try {
        await this.confirmLicenseAgreement();
        return this.generate(entityType, entityId, focus, snippetId, modelToUpdate);
      } catch (e) {
        return {
          message: 'You must agree to the disclaimer to continue.',
          type: 'error'
        };
      }
    }
    const clientRequestId = Math.random().toString(36).substring(2, 15);
    this.llmRequests.set(clientRequestId, modelToUpdate);
    const result = await this.apiClient.post<AIChatResult>(`generation/generate`, {
      entityType,
      entityId,
      focus,
      snippetId,
      clientRequestId
    });
    modelToUpdate.finishStreaming();
    return result;
  }

  getRegistrationToken() {
    return this.registrationTokenProvider.registrationToken;
  }

  private subscribeToSocketIo() {
    this.registrationTokenProvider.registrationToken = Math.random().toString(36).substring(7);
    this.socket = new Socket({
      url: environment.aiApiUrl,
      options: {
        extraHeaders: {
          Authorization: `Bearer ${this.authService.jwtToken}`,
        },
        transports: ['websocket']
      }
    });
    this.socket.emit('register', this.registrationTokenProvider.registrationToken);
    this.socket.on('chat-live-feedback', (data: any) => {
      this.chatUpdateEvent.emit(data);
    });
    this.socket.on('llm_new_token', (data: {reqId: string, newToken: string}) => {
      const message = this.llmRequests.get(data.reqId);
      if (!message) {
        return;
      }
      message.addToken(data.newToken);
      message.startStreaming();
    });
  }

  private async getAssistants() {
    const result = await this.apiClient.get<any>(`${this.baseUrl}`);
    if (!result) {
      return;
    }
    this.assistants = result;
  }


}

export interface AssistantDefinition {
  name: string;
  availability: string;
  description: string;
  image: string;
  role: string;
  key: string;
}

export interface AIChatUpdateMessage {
  message: string;
}

export interface AIChatResult {
  message: string;
  type: string;
}

